import React from 'react';
import { Box, Typography, Paper, Chip } from '@mui/material';
import { SectionVariable } from '../types';
import { labelTagSx } from '../theme/tokens';
import { useSettings } from '../Settings';
import { useTranslation } from '../i18n';

interface BenefitsProps {
  variables?: SectionVariable;
  name?: string;
}

const IconWhatsappOutline = () => (
  <svg width="1em" height="1em" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"/>
    <path d="M16.5 13.5a1.5 1.5 0 0 1-1.5 1.5c-2.5 0-4.5-2-4.5-4.5a1.5 1.5 0 0 1 1.5-1.5h1l1 2-1 1a5 5 0 0 0 2.5 2.5l1-1 2 1v1z"/>
  </svg>
);

const IconHeartOutline = () => (
  <svg width="1em" height="1em" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20.8 4.6a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.02-1.07a5.5 5.5 0 0 0-7.78 7.78l1.02 1.07L12 21.23l7.78-7.78 1.02-1.07a5.5 5.5 0 0 0 0-7.78z"/>
  </svg>
);

const IconSpeechCheck = () => (
  <svg width="1em" height="1em" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"/>
    <path d="M9 12l2 2 4-4"/>
  </svg>
);

const IconSquarePlus = () => (
  <svg width="1em" height="1em" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="3" width="18" height="18" rx="4" ry="4"/>
    <line x1="12" y1="8" x2="12" y2="16"/>
    <line x1="8" y1="12" x2="16" y2="12"/>
  </svg>
);

const IconGlobe = () => (
  <svg width="1em" height="1em" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"/>
    <line x1="2" y1="12" x2="22" y2="12"/>
    <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
  </svg>
);

const IconSmartphone = () => (
  <svg width="1em" height="1em" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <rect x="5" y="2" width="14" height="20" rx="3" />
    <path d="M9 18h6" />
  </svg>
);

const IconSimCard = () => (
  <svg width="1em" height="1em" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M9 8V6C9 4.34 10.34 3 12 3H17C18.66 3 20 4.34 20 6V15C20 16.66 18.66 18 17 18H15" />
    <path d="M15 8H8C6.34 8 5 9.34 5 11V20C5 21.66 6.34 23 8 23H15C16.66 23 18 21.66 18 20V11C18 9.34 16.66 8 15 8Z" />
    <rect x="8" y="12" width="6" height="8" rx="1.5" />
  </svg>
);

const IconSpeechLines = () => (
  <svg width="1em" height="1em" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"/>
    <line x1="9" y1="9" x2="9" y2="15"/>
    <line x1="12" y1="7" x2="12" y2="17"/>
    <line x1="15" y1="9" x2="15" y2="15"/>
  </svg>
);

const benefitIcons = [
  <IconWhatsappOutline />,
  <IconHeartOutline />,
  <IconSpeechCheck />,
  <IconSquarePlus />,
  <IconGlobe />,
  <IconSmartphone />,
  <IconSimCard />,
  <IconSpeechLines />,
];

export function Benefits({ variables: _variables }: BenefitsProps) {
  const { settings } = useSettings();
  const { t } = useTranslation();

  const benefitItems = t.benefits.items.map((entry, i) => ({
    icon: benefitIcons[i] || benefitIcons[0],
    title: entry.title,
    description: entry.description,
  }));

  const displayedBenefits = benefitItems.filter((item) => {
    const isWA = item.title.toLowerCase().includes('whatsapp');
    const isWZ = item.title.toLowerCase().includes('waze');
    return !(settings.isSurfMVNE && (isWA || isWZ));
  });

  return (
    <Box
      id="vantagens"
      className="benefits"
      sx={{
        py: { xs: 6, md: 8 },
        px: { xs: 2, md: 4 },
        maxWidth: 1280,
        mx: 'auto',
        position: 'relative',
        zIndex: 10,
      }}
    >
      <Chip
        label={t.benefits.tag}
        size="small"
        sx={{
          ...labelTagSx,
          mb: 2,
          px: 0.5,
        }}
      />
      <Typography
        variant="h2"
        sx={{
          color: '#404040',
          fontFamily: "'Figtree', sans-serif",
          fontSize: { xs: '1.8rem', md: '2.1875rem' },
          fontStyle: 'normal',
          fontWeight: 500,
          lineHeight: { xs: '2.2rem', md: '2.5rem' },
          mb: 4,
          letterSpacing: '-0.02em',
        }}
      >
        {t.benefits.titleLine1}
        <br />
        {t.benefits.titleLine2}
      </Typography>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(5, 1fr)' },
          gap: 3,
          gridAutoFlow: 'dense',
        }}
      >
        {displayedBenefits.map((item, index) => {
          // Fallback logic for when items are removed
          const isWide = displayedBenefits.length === 8 ? (index === 3 || index === 4) : false;
          
          let pos = { col: 'auto' as const, row: 'auto' };
          let isTallMobile = false;

          if (displayedBenefits.length === 8) {
            const mobilePos: Record<number, { col: number; row: string }> = {
              4: { col: 1, row: '1 / 3' },   // tall
              5: { col: 1, row: '3 / 4' },
              6: { col: 1, row: '4 / 5' },
              7: { col: 1, row: '5 / 6' },
              0: { col: 2, row: '1 / 2' },
              1: { col: 2, row: '2 / 3' },
              2: { col: 2, row: '3 / 4' },
              3: { col: 2, row: '4 / 6' },   // tall
            };
            pos = mobilePos[index] as any;
            isTallMobile = index === 4 || index === 3;
          }

          return (
            <Box
              key={index}
              sx={{
                gridColumn: { xs: pos.col, md: isWide ? 'span 2' : 'span 1' },
                gridRow: { xs: pos.row, md: 'auto' },
              }}
            >
              <Paper
                elevation={0}
                sx={{
                  p: { xs: '1.5rem', md: '2.5rem' },
                  borderRadius: '24px',
                  bgcolor: '#F5F5F5',
                  height: { xs: '100%', md: '19.25rem' },
                  minHeight: { xs: isTallMobile ? '25rem' : '12rem', md: 'auto' },
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  transition: 'background-color 0.2s',
                  '&:hover': {
                    bgcolor: '#eeeeee',
                  },
                }}
              >
                <Box>
                  <Box
                    sx={{
                      color: '#1a1a1a',
                      mb: 4,
                      '& svg': { fontSize: '1.8rem', strokeWidth: 1 },
                    }}
                  >
                    {item.icon}
                  </Box>
                  <Typography
                    sx={{
                      color: '#2E2E2E',
                      fontFamily: "'Poppins', sans-serif",
                      fontSize: '1.125rem',
                      fontStyle: 'normal',
                      fontWeight: 500,
                      lineHeight: 'normal',
                      maxWidth: '80%',
                    }}
                  >
                    {item.title}
                  </Typography>
                </Box>
                <Typography
                  sx={{
                    color: '#A0A0A0',
                    fontFamily: "'Figtree', sans-serif",
                    fontSize: '0.875rem',
                    fontStyle: 'normal',
                    fontWeight: 400,
                    lineHeight: 'normal',
                    maxWidth: '90%',
                  }}
                >
                  {item.description}
                </Typography>
              </Paper>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}
